import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Keyboard, Package, Plus, RefreshCw, Edit3, Trash2, Check, X, Wrench } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = 'https://baustellenmgmt.mas123.de/api';

const KATEGORIE_OPTIONS = [
  { value: 'GERAET', label: 'Gerät' },
  { value: 'MASCHINE', label: 'Maschine' },
  { value: 'HILFSMITTEL', label: 'Hilfsmittel' },
  { value: 'LAGER', label: 'Lagerartikel' },
  { value: 'FREI', label: 'Freie Position' },
];

const DEFAULT_ARTIKEL = {
  artikelnummer: '',
  name: '',
  beschreibung: '',
  einheit: '',
  aktiv: true,
};

const DEFAULT_PREIS = {
  artikel: '',
  preis_pro_einheit: '',
  gueltig_ab: '',
};

const DEFAULT_POSITION = {
  baustelle: '',
  datum: new Date().toISOString().slice(0, 10),
  kategorie: 'GERAET',
  artikel: '',
  bezeichnung: '',
  menge: '1',
  einheit: '',
  preis_pro_einheit: '',
  gesamtpreis: '',
  bemerkung: '',
};

function compareByLabelDe(a, b) {
  return String(a || '').localeCompare(String(b || ''), 'de', { sensitivity: 'base' });
}

function formatAmountDe(value) {
  if (value === null || value === undefined || value === '') return '-';
  const num = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function toNumber(value) {
  if (value === null || value === undefined || value === '') return 0;
  const num = Number(String(value).replace(',', '.'));
  return Number.isNaN(num) ? 0 : num;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function getBaustelleLabel(baustelle) {
  if (!baustelle) return '';
  return baustelle.kunde_name || baustelle.name || baustelle.nummer || '';
}

function getArtikelLabel(artikel) {
  if (!artikel) return '';
  return `${artikel.name || ''}${artikel.artikelnummer ? ` (${artikel.artikelnummer})` : ''}`.trim();
}

function calculateGesamtpreis(menge, preis) {
  const quantity = toNumber(menge);
  const unitPrice = toNumber(preis);
  if (!quantity || !unitPrice) return '';
  return String((quantity * unitPrice).toFixed(2)).replace('.', ',');
}

function stepNumericInput(value, delta) {
  const current = toNumber(value);
  const next = Math.max(0, current + delta);
  return Number.isInteger(next) ? String(next) : String(next.toFixed(2)).replace('.', ',');
}

function MaterialerfassungView({ token }) {
  const [artikel, setArtikel] = useState([]);
  const [baustellen, setBaustellen] = useState([]);
  const [kostenpositionen, setKostenpositionen] = useState([]);
  const [selectedKostenpositionIds, setSelectedKostenpositionIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [kostenLoading, setKostenLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedArtikelId, setSelectedArtikelId] = useState('');
  const [selectedBaustelleId, setSelectedBaustelleId] = useState('');
  const [editingArtikel, setEditingArtikel] = useState(null);
  const [newArtikel, setNewArtikel] = useState(null);
  const [editingPreis, setEditingPreis] = useState(null);
  const [newPreis, setNewPreis] = useState(null);
  const [editingPosition, setEditingPosition] = useState(null);
  const [newPosition, setNewPosition] = useState(null);
  const [showArtikelverwaltung, setShowArtikelverwaltung] = useState(false);
  const [showKeyboardEntry, setShowKeyboardEntry] = useState(false);

  const loadArtikel = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/artikel/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page_size: 500 },
      });
      const list = response.data.results || response.data || [];
      setArtikel(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Fehler beim Laden des Artikelstamms:', error);
      alert('Fehler beim Laden des Artikelstamms');
    }
  }, [token]);

  const loadBaustellen = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/baustellen/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page_size: 500 },
      });
      const list = response.data.results || response.data || [];
      const sorted = (Array.isArray(list) ? list : []).sort(
        (a, b) => compareByLabelDe(a.kunde_name || a.name, b.kunde_name || b.name) || compareByLabelDe(a.name, b.name)
      );
      setBaustellen(sorted);
      setSelectedBaustelleId((current) => current || String(sorted[0]?.id || ''));
    } catch (error) {
      console.error('Fehler beim Laden der Baustellen:', error);
      alert('Fehler beim Laden der Baustellen');
    }
  }, [token]);

  const loadKostenpositionen = useCallback(async (baustelleId) => {
    if (!baustelleId) {
      setKostenpositionen([]);
      setSelectedKostenpositionIds([]);
      return;
    }
    setKostenLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/baustellen/kostenpositionen/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { baustelle: baustelleId, page_size: 500 },
      });
      const list = response.data.results || response.data || [];
      setKostenpositionen(Array.isArray(list) ? list : []);
      setSelectedKostenpositionIds([]);
    } catch (error) {
      console.error('Fehler beim Laden der Kostenpositionen:', error);
      alert('Fehler beim Laden der Kostenpositionen');
      setKostenpositionen([]);
      setSelectedKostenpositionIds([]);
    }
    setKostenLoading(false);
  }, [token]);

  useEffect(() => {
    let mounted = true;
    async function loadAll() {
      setLoading(true);
      await Promise.all([loadArtikel(), loadBaustellen()]);
      if (mounted) setLoading(false);
    }
    loadAll();
    return () => {
      mounted = false;
    };
  }, [loadArtikel, loadBaustellen]);

  useEffect(() => {
    loadKostenpositionen(selectedBaustelleId);
  }, [loadKostenpositionen, selectedBaustelleId]);

  const filteredArtikel = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return [...artikel]
      .filter((entry) => {
        const matchSearch = !needle || [entry.artikelnummer, entry.name, entry.beschreibung, entry.einheit]
          .some((value) => String(value || '').toLowerCase().includes(needle));
        const matchActive = showInactive || entry.aktiv;
        return matchSearch && matchActive;
      })
      .sort((a, b) => compareByLabelDe(a.name, b.name) || compareByLabelDe(a.artikelnummer, b.artikelnummer));
  }, [artikel, search, showInactive]);

  useEffect(() => {
    if (!selectedArtikelId && filteredArtikel.length > 0) {
      setSelectedArtikelId(String(filteredArtikel[0].id));
      return;
    }
    if (selectedArtikelId && !filteredArtikel.some((entry) => String(entry.id) === String(selectedArtikelId))) {
      setSelectedArtikelId(filteredArtikel[0] ? String(filteredArtikel[0].id) : '');
    }
  }, [filteredArtikel, selectedArtikelId]);

  const selectedArtikel = filteredArtikel.find((entry) => String(entry.id) === String(selectedArtikelId))
    || artikel.find((entry) => String(entry.id) === String(selectedArtikelId))
    || null;

  const selectedBaustelle = baustellen.find((entry) => String(entry.id) === String(selectedBaustelleId)) || null;

  const sortedArtikelOptions = useMemo(
    () => [...artikel].filter((entry) => entry.aktiv).sort((a, b) => compareByLabelDe(a.name, b.name) || compareByLabelDe(a.artikelnummer, b.artikelnummer)),
    [artikel]
  );

  const kostenGesamt = useMemo(
    () => kostenpositionen.reduce((sum, entry) => sum + toNumber(entry.gesamtpreis), 0),
    [kostenpositionen]
  );

  const allKostenpositionenSelected = useMemo(
    () => kostenpositionen.length > 0 && kostenpositionen.every((entry) => selectedKostenpositionIds.includes(entry.id)),
    [kostenpositionen, selectedKostenpositionIds]
  );

  async function refreshAll() {
    setLoading(true);
    await Promise.all([loadArtikel(), loadBaustellen(), loadKostenpositionen(selectedBaustelleId)]);
    setLoading(false);
  }

  async function saveArtikel(item) {
    const payload = {
      artikelnummer: (item.artikelnummer || '').trim(),
      name: (item.name || '').trim(),
      beschreibung: item.beschreibung || '',
      einheit: (item.einheit || '').trim(),
      aktiv: item.aktiv ?? true,
    };
    if (!payload.artikelnummer || !payload.name || !payload.einheit) {
      alert('Bitte Artikelnummer, Name und Einheit ausfüllen.');
      return;
    }

    try {
      if (item.id) {
        await axios.put(`${API_BASE_URL}/baustellen/artikel/${item.id}/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_BASE_URL}/baustellen/artikel/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setEditingArtikel(null);
      setNewArtikel(null);
      await loadArtikel();
    } catch (error) {
      console.error('Fehler beim Speichern des Artikels:', error);
      alert(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Speichern des Artikels');
    }
  }

  async function deleteArtikel(id) {
    if (!window.confirm('Artikel wirklich löschen?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/baustellen/artikel/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (String(selectedArtikelId) === String(id)) {
        setSelectedArtikelId('');
      }
      await loadArtikel();
    } catch (error) {
      console.error('Fehler beim Löschen des Artikels:', error);
      alert(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Löschen des Artikels');
    }
  }

  async function savePreis(item) {
    const payload = {
      artikel: Number(item.artikel || selectedArtikel?.id),
      preis_pro_einheit: String(item.preis_pro_einheit || '').replace(',', '.'),
      gueltig_ab: item.gueltig_ab || '',
    };
    if (!payload.artikel || !payload.preis_pro_einheit || !payload.gueltig_ab) {
      alert('Bitte Artikel, Preis und Gültig-ab ausfüllen.');
      return;
    }

    try {
      if (item.id) {
        await axios.put(`${API_BASE_URL}/baustellen/artikelpreise/${item.id}/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_BASE_URL}/baustellen/artikelpreise/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setEditingPreis(null);
      setNewPreis(null);
      await loadArtikel();
    } catch (error) {
      console.error('Fehler beim Speichern des Preisstands:', error);
      alert(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Speichern des Preisstands');
    }
  }

  async function deletePreis(id) {
    if (!window.confirm('Preisstand wirklich löschen?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/baustellen/artikelpreise/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadArtikel();
    } catch (error) {
      console.error('Fehler beim Löschen des Preisstands:', error);
      alert(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Löschen des Preisstands');
    }
  }

  async function savePosition(item) {
    const payload = {
      baustelle: Number(item.baustelle || selectedBaustelleId),
      datum: item.datum || '',
      kategorie: item.kategorie || 'GERAET',
      artikel: item.artikel ? Number(item.artikel) : null,
      bezeichnung: (item.bezeichnung || '').trim(),
      menge: String(item.menge || '').replace(',', '.'),
      einheit: item.einheit || '',
      preis_pro_einheit: item.preis_pro_einheit === '' ? null : String(item.preis_pro_einheit).replace(',', '.'),
      gesamtpreis: item.gesamtpreis === '' ? null : String(item.gesamtpreis).replace(',', '.'),
      bemerkung: item.bemerkung || '',
    };
    if (!payload.baustelle || !payload.datum || !payload.kategorie || !payload.menge) {
      alert('Bitte Baustelle, Datum, Kategorie und Menge ausfüllen.');
      return;
    }

    try {
      if (item.id) {
        await axios.put(`${API_BASE_URL}/baustellen/kostenpositionen/${item.id}/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_BASE_URL}/baustellen/kostenpositionen/`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setEditingPosition(null);
      setNewPosition(null);
      await loadKostenpositionen(payload.baustelle);
    } catch (error) {
      console.error('Fehler beim Speichern der Kostenposition:', error);
      alert(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Speichern der Kostenposition');
    }
  }

  async function deletePosition(id) {
    if (!window.confirm('Kostenposition wirklich löschen?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/baustellen/kostenpositionen/${id}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadKostenpositionen(selectedBaustelleId);
    } catch (error) {
      console.error('Fehler beim Löschen der Kostenposition:', error);
      alert(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Löschen der Kostenposition');
    }
  }

  async function deleteSelectedPositionen() {
    if (selectedKostenpositionIds.length === 0) {
      alert('Bitte mindestens einen Eintrag auswählen.');
      return;
    }
    if (!window.confirm(`${selectedKostenpositionIds.length} ausgewählte Kostenpositionen wirklich löschen?`)) {
      return;
    }
    const idsToDelete = Array.from(new Set(selectedKostenpositionIds.map((id) => String(id)).filter(Boolean)));
    try {
      const results = await Promise.allSettled(idsToDelete.map((id) => (
        axios.delete(`${API_BASE_URL}/baustellen/kostenpositionen/${id}/`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      )));
      const failed = results.filter((result) => result.status === 'rejected');
      if (failed.length > 0) {
        const firstError = failed[0].reason;
        throw firstError;
      }
      setKostenpositionen((prev) => prev.filter((entry) => !idsToDelete.includes(String(entry.id))));
      setSelectedKostenpositionIds([]);
      await loadKostenpositionen(selectedBaustelleId);
    } catch (error) {
      console.error('Fehler beim Löschen der ausgewählten Kostenpositionen:', error);
      alert(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Löschen der ausgewählten Kostenpositionen');
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Package size={28} />
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>Materialerfassung</h2>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={refreshAll} className="btn" disabled={loading || kostenLoading}>
            <RefreshCw size={18} className={loading || kostenLoading ? 'spinning' : ''} /> Aktualisieren
          </button>
          <button onClick={() => setShowKeyboardEntry(true)} className="btn btn-primary">
            <Keyboard size={18} /> Tastatur-Erfassung
          </button>
        </div>
      </div>

      {showKeyboardEntry && (
        <KeyboardMaterialEntryModal
          token={token}
          baustellen={baustellen}
          artikelOptionen={sortedArtikelOptions}
          onClose={() => setShowKeyboardEntry(false)}
          onSaved={async (baustelleId) => {
            if (baustelleId) {
              setSelectedBaustelleId(String(baustelleId));
            }
            await loadKostenpositionen(baustelleId || selectedBaustelleId);
          }}
        />
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Wrench size={24} />
            <div>
              <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Baustellen-Kostenpositionen</h3>
              <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Geräte, Maschinen, Hilfsmittel, Lagerartikel und freie Positionen pro Baustelle
              </div>
            </div>
          </div>
          {!newPosition && !editingPosition && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                className="btn"
                style={{ color: '#dc2626' }}
                onClick={deleteSelectedPositionen}
                disabled={selectedKostenpositionIds.length === 0}
              >
                <Trash2 size={16} /> Ausgewählte löschen
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setNewPosition({ ...DEFAULT_POSITION, baustelle: selectedBaustelleId || '' })}
                disabled={!selectedBaustelleId}
              >
                <Plus size={16} /> Position hinzufügen
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 420px) auto', gap: '1rem', alignItems: 'center' }}>
          <select
            className="input"
            value={selectedBaustelleId}
            onChange={(e) => {
              setSelectedBaustelleId(e.target.value);
              setEditingPosition(null);
              setNewPosition(null);
            }}
          >
            <option value="">Baustelle wählen...</option>
            {baustellen.map((baustelle) => (
              <option key={baustelle.id} value={baustelle.id}>
                {(baustelle.kunde_name || baustelle.name)} - {baustelle.nummer}
              </option>
            ))}
          </select>
          <div style={{ color: '#374151', fontSize: '0.9rem', textAlign: 'right' }}>
            {selectedBaustelle ? `Gesamt: ${formatAmountDe(kostenGesamt)} EUR` : 'Bitte Baustelle auswählen'}
          </div>
        </div>
      </div>

      {(newPosition || editingPosition) && (
        <KostenpositionForm
          position={editingPosition || newPosition}
          token={token}
          artikelOptionen={sortedArtikelOptions}
          baustellen={baustellen}
          onChange={(item) => (editingPosition ? setEditingPosition(item) : setNewPosition(item))}
          onSave={() => savePosition(editingPosition || newPosition)}
          onCancel={() => {
            setEditingPosition(null);
            setNewPosition(null);
          }}
        />
      )}

      <div className="card" style={{ margin: 0 }}>
        {!selectedBaustelleId ? (
          <div style={{ color: '#6b7280' }}>Bitte oben eine Baustelle auswählen.</div>
        ) : kostenLoading ? (
          <div style={{ color: '#6b7280' }}>Lade Kostenpositionen...</div>
        ) : kostenpositionen.length === 0 ? (
          <div style={{ color: '#6b7280' }}>Noch keine Kostenpositionen für diese Baustelle vorhanden.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                  <th style={{ padding: '0.75rem 0.5rem', width: '44px' }}>
                    <input
                      type="checkbox"
                      checked={allKostenpositionenSelected}
                      onChange={(e) => {
                        setSelectedKostenpositionIds(e.target.checked ? kostenpositionen.map((entry) => entry.id) : []);
                      }}
                      title="Alle auswählen"
                    />
                  </th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Datum</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Kategorie</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}>Artikel / Bezeichnung</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Menge</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Preis/EH</th>
                  <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Gesamt</th>
                  <th style={{ padding: '0.75rem 0.5rem' }}></th>
                </tr>
              </thead>
              <tbody>
                {kostenpositionen.map((position) => (
                  <tr key={position.id} style={{ borderTop: '1px solid #e5e7eb', verticalAlign: 'top' }}>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedKostenpositionIds.includes(position.id)}
                        onChange={(e) => {
                          setSelectedKostenpositionIds((prev) => (
                            e.target.checked
                              ? [...prev, position.id]
                              : prev.filter((id) => id !== position.id)
                          ));
                        }}
                      />
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>{position.datum}</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      {KATEGORIE_OPTIONS.find((entry) => entry.value === position.kategorie)?.label || position.kategorie}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <div style={{ fontWeight: 600 }}>{position.bezeichnung}</div>
                      <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                        {position.artikel_name ? `${position.artikel_name} (${position.artikel_nummer || '-'})` : 'Freie Eingabe'}
                      </div>
                      {position.bemerkung && (
                        <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: '0.25rem' }}>{position.bemerkung}</div>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>
                      {formatAmountDe(position.menge)} {position.einheit || ''}
                    </td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>{formatAmountDe(position.preis_pro_einheit)} EUR</td>
                    <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>{formatAmountDe(position.gesamtpreis)} EUR</td>
                    <td style={{ padding: '0.75rem 0.5rem' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button className="btn" onClick={() => setEditingPosition({
                          ...position,
                          baustelle: position.baustelle ? String(position.baustelle) : '',
                          artikel: position.artikel ? String(position.artikel) : '',
                          menge: position.menge ?? '',
                          preis_pro_einheit: position.preis_pro_einheit ?? '',
                          gesamtpreis: position.gesamtpreis ?? '',
                        })}>
                          <Edit3 size={16} />
                        </button>
                        <button className="btn" style={{ color: '#dc2626' }} onClick={() => deletePosition(position.id)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid #d1d5db' }}>
                  <td colSpan="6" style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: 700 }}>Summe</td>
                  <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: 700 }}>{formatAmountDe(kostenGesamt)} EUR</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: '2rem' }}>
        <button
          type="button"
          className="btn"
          onClick={() => setShowArtikelverwaltung((prev) => !prev)}
          style={{
            width: '100%',
            justifyContent: 'space-between',
            fontWeight: 700,
            background: '#f9fafb',
          }}
        >
          <span>Artikelverwaltung</span>
          <span>{showArtikelverwaltung ? 'Ausblenden' : 'Einblenden'}</span>
        </button>
      </div>

      {showArtikelverwaltung && (
        <>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>Artikelverwaltung</div>
              {!newArtikel && !editingArtikel && (
                <button onClick={() => setNewArtikel({ ...DEFAULT_ARTIKEL })} className="btn btn-primary">
                  <Plus size={18} /> Neuer Artikel
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr auto', gap: '1rem', alignItems: 'center' }}>
              <input
                type="text"
                className="input"
                placeholder="Artikel suchen..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  style={{ width: '1.05rem', height: '1.05rem', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.875rem', color: '#374151' }}>Inaktive anzeigen</span>
              </label>
            </div>
          </div>

          {(newArtikel || editingArtikel) && (
            <ArtikelForm
              artikel={editingArtikel || newArtikel}
              onChange={(item) => (editingArtikel ? setEditingArtikel(item) : setNewArtikel(item))}
              onSave={() => saveArtikel(editingArtikel || newArtikel)}
              onCancel={() => {
                setEditingArtikel(null);
                setNewArtikel(null);
              }}
            />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
            <div className="card" style={{ margin: 0 }}>
              {loading && artikel.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Lade Artikel...</div>
              ) : filteredArtikel.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Keine Artikel gefunden</div>
              ) : (
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  {filteredArtikel.map((entry) => {
                    const isSelected = String(entry.id) === String(selectedArtikelId);
                    return (
                      <button
                        key={entry.id}
                        className="btn"
                        onClick={() => setSelectedArtikelId(String(entry.id))}
                        style={{
                          justifyContent: 'space-between',
                          textAlign: 'left',
                          background: isSelected ? '#dbeafe' : (entry.aktiv ? '#ffffff' : '#f9fafb'),
                          borderColor: isSelected ? '#3b82f6' : '#e5e7eb',
                        }}
                      >
                        <span>
                          <strong>{entry.name}</strong>
                          <span style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280' }}>
                            {entry.artikelnummer} • {entry.einheit}
                          </span>
                        </span>
                        {!entry.aktiv && <span style={{ fontSize: '0.75rem', color: '#b91c1c' }}>Inaktiv</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="card" style={{ margin: 0 }}>
              {!selectedArtikel ? (
                <div style={{ color: '#6b7280' }}>Bitte links einen Artikel auswählen.</div>
              ) : (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1.2rem' }}>{selectedArtikel.name}</h3>
                      <div style={{ color: '#6b7280', marginTop: '0.25rem' }}>
                        {selectedArtikel.artikelnummer} • Einheit: {selectedArtikel.einheit}
                      </div>
                      {selectedArtikel.beschreibung && (
                        <div style={{ color: '#374151', marginTop: '0.5rem' }}>{selectedArtikel.beschreibung}</div>
                      )}
                      <div style={{ color: '#374151', marginTop: '0.5rem' }}>
                        Aktueller Preis: <strong>{formatAmountDe(selectedArtikel.aktueller_preis?.preis_pro_einheit)} EUR</strong>
                        {' '}ab {selectedArtikel.aktueller_preis?.gueltig_ab || '-'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button className="btn" onClick={() => setEditingArtikel({ ...selectedArtikel })}>
                        <Edit3 size={16} /> Bearbeiten
                      </button>
                      <button className="btn" style={{ color: '#dc2626' }} onClick={() => deleteArtikel(selectedArtikel.id)}>
                        <Trash2 size={16} /> Löschen
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <h4 style={{ margin: 0 }}>Preisstände</h4>
                    {!newPreis && !editingPreis && (
                      <button
                        className="btn btn-primary"
                        onClick={() => setNewPreis({
                          ...DEFAULT_PREIS,
                          artikel: selectedArtikel.id,
                          gueltig_ab: new Date().toISOString().slice(0, 10),
                        })}
                      >
                        <Plus size={16} /> Preisstand hinzufügen
                      </button>
                    )}
                  </div>

                  {newPreis && (
                    <PreisForm
                      preis={newPreis}
                      onChange={setNewPreis}
                      onSave={() => savePreis(newPreis)}
                      onCancel={() => setNewPreis(null)}
                    />
                  )}

                  {editingPreis && (
                    <PreisForm
                      preis={editingPreis}
                      onChange={setEditingPreis}
                      onSave={() => savePreis(editingPreis)}
                      onCancel={() => setEditingPreis(null)}
                    />
                  )}

                  {!editingPreis && !newPreis && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                        <thead>
                          <tr style={{ textAlign: 'left', color: '#6b7280' }}>
                            <th style={{ padding: '0.75rem 0.5rem' }}>Gültig ab</th>
                            <th style={{ padding: '0.75rem 0.5rem', textAlign: 'right' }}>Preis</th>
                            <th style={{ padding: '0.75rem 0.5rem' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(selectedArtikel.preise || []).length === 0 ? (
                            <tr>
                              <td colSpan="3" style={{ padding: '1rem 0.5rem', color: '#6b7280' }}>
                                Noch keine Preisstände vorhanden.
                              </td>
                            </tr>
                          ) : (
                            (selectedArtikel.preise || []).map((preis) => (
                              <tr key={preis.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                <td style={{ padding: '0.75rem 0.5rem' }}>{preis.gueltig_ab}</td>
                                <td style={{ padding: '0.75rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>
                                  {formatAmountDe(preis.preis_pro_einheit)} EUR
                                </td>
                                <td style={{ padding: '0.75rem 0.5rem' }}>
                                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                                    <button className="btn" onClick={() => setEditingPreis({ ...preis, artikel: selectedArtikel.id })}>
                                      <Edit3 size={16} />
                                    </button>
                                    <button className="btn" style={{ color: '#dc2626' }} onClick={() => deletePreis(preis.id)}>
                                      <Trash2 size={16} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ArtikelForm({ artikel, onChange, onSave, onCancel }) {
  return (
    <div className="card" style={{ marginBottom: '1.5rem', background: '#f9fafb' }}>
      <div style={{ display: 'grid', gap: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '1rem' }}>
          <input className="input" placeholder="Artikelnummer" value={artikel.artikelnummer} onChange={(e) => onChange({ ...artikel, artikelnummer: e.target.value })} />
          <input className="input" placeholder="Name" value={artikel.name} onChange={(e) => onChange({ ...artikel, name: e.target.value })} />
          <input className="input" placeholder="Einheit" value={artikel.einheit} onChange={(e) => onChange({ ...artikel, einheit: e.target.value })} />
        </div>
        <textarea className="input" rows={3} placeholder="Beschreibung" value={artikel.beschreibung} onChange={(e) => onChange({ ...artikel, beschreibung: e.target.value })} />
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={artikel.aktiv ?? true} onChange={(e) => onChange({ ...artikel, aktiv: e.target.checked })} style={{ width: '1.05rem', height: '1.05rem', cursor: 'pointer' }} />
          <span style={{ fontSize: '0.875rem', color: '#374151' }}>Aktiv</span>
        </label>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onCancel}><X size={16} /> Abbrechen</button>
          <button className="btn btn-primary" onClick={onSave}><Check size={16} /> Speichern</button>
        </div>
      </div>
    </div>
  );
}

function PreisForm({ preis, onChange, onSave, onCancel }) {
  return (
    <div className="card" style={{ marginBottom: '1rem', background: '#f9fafb' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <input type="date" className="input" value={preis.gueltig_ab} onChange={(e) => onChange({ ...preis, gueltig_ab: e.target.value })} />
        <input className="input" placeholder="Preis pro Einheit" value={preis.preis_pro_einheit} onChange={(e) => onChange({ ...preis, preis_pro_einheit: e.target.value })} />
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onCancel}><X size={16} /> Abbrechen</button>
        <button className="btn btn-primary" onClick={onSave}><Check size={16} /> Speichern</button>
      </div>
    </div>
  );
}

function KostenpositionForm({ position, token, artikelOptionen, baustellen, onChange, onSave, onCancel }) {
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const query = (position.bezeichnung || '').trim();
    if (query.length < 2) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setSuggestionsLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/baustellen/kostenpositionen/vorschlaege/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            q: query,
            kategorie: position.kategorie || '',
          },
        });
        if (!cancelled) {
          setSuggestions(response.data.results || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Fehler beim Laden der Material-Vorschläge:', error);
          setSuggestions([]);
        }
      }
      if (!cancelled) {
        setSuggestionsLoading(false);
      }
    }, 180);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [position.bezeichnung, position.kategorie, token]);

  function applySuggestion(suggestion) {
    onChange({
      ...position,
      artikel: suggestion.artikel_id ? String(suggestion.artikel_id) : '',
      bezeichnung: suggestion.bezeichnung || '',
      einheit: suggestion.einheit || '',
      preis_pro_einheit: suggestion.preis_pro_einheit || '',
      gesamtpreis: '',
    });
    setShowSuggestions(false);
  }

  return (
    <div className="card" style={{ marginBottom: '1rem', background: '#f9fafb' }}>
      <div style={{ display: 'grid', gap: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr', gap: '1rem' }}>
          <select className="input" value={position.baustelle || ''} onChange={(e) => onChange({ ...position, baustelle: e.target.value })}>
            <option value="">Baustelle wählen...</option>
            {baustellen.map((baustelle) => (
              <option key={baustelle.id} value={baustelle.id}>
                {(baustelle.kunde_name || baustelle.name)} - {baustelle.nummer}
              </option>
            ))}
          </select>
          <input type="date" className="input" value={position.datum || ''} onChange={(e) => onChange({ ...position, datum: e.target.value })} />
          <select className="input" value={position.kategorie || 'GERAET'} onChange={(e) => onChange({ ...position, kategorie: e.target.value })}>
            {KATEGORIE_OPTIONS.map((entry) => (
              <option key={entry.value} value={entry.value}>{entry.label}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: '1rem' }}>
          <div style={{ position: 'relative' }}>
            <input
              className="input"
              placeholder="Bezeichnung"
              value={position.bezeichnung || ''}
              onChange={(e) => {
                onChange({ ...position, bezeichnung: e.target.value });
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => {
                setTimeout(() => setShowSuggestions(false), 150);
              }}
            />
            {showSuggestions && ((position.bezeichnung || '').trim().length >= 2) && (
              <div
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 0.25rem)',
                  left: 0,
                  right: 0,
                  background: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.75rem',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
                  zIndex: 20,
                  maxHeight: '240px',
                  overflowY: 'auto',
                }}
              >
                {suggestionsLoading ? (
                  <div style={{ padding: '0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>Suche Vorschläge...</div>
                ) : suggestions.length === 0 ? (
                  <div style={{ padding: '0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>Keine Vorschläge gefunden</div>
                ) : (
                  suggestions.map((suggestion, index) => (
                    <button
                      key={`${suggestion.source}-${suggestion.artikel_id || 'frei'}-${index}`}
                      type="button"
                      className="btn"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applySuggestion(suggestion)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        justifyContent: 'space-between',
                        border: 'none',
                        borderBottom: index === suggestions.length - 1 ? 'none' : '1px solid #e5e7eb',
                        borderRadius: 0,
                        background: suggestion.source === 'artikelstamm' ? '#eff6ff' : '#ffffff',
                        padding: '0.75rem',
                      }}
                    >
                      <span>
                        <strong>{suggestion.bezeichnung}</strong>
                        <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280' }}>
                          {suggestion.source === 'artikelstamm' ? 'Artikelstamm' : 'Frühere Eingabe'}
                          {suggestion.artikelnummer ? ` • ${suggestion.artikelnummer}` : ''}
                          {suggestion.einheit ? ` • ${suggestion.einheit}` : ''}
                          {suggestion.preis_pro_einheit ? ` • ${formatAmountDe(suggestion.preis_pro_einheit)} EUR` : ''}
                        </span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <select
            className="input"
            value={position.artikel || ''}
            onChange={(e) => {
              const artikelId = e.target.value;
              const artikel = artikelOptionen.find((entry) => String(entry.id) === String(artikelId));
              onChange({
                ...position,
                artikel: artikelId,
                bezeichnung: position.bezeichnung || artikel?.name || '',
                einheit: position.einheit || artikel?.einheit || '',
                preis_pro_einheit: '',
                gesamtpreis: '',
              });
            }}
          >
            <option value="">Ohne Artikelreferenz</option>
            {artikelOptionen.map((artikel) => (
              <option key={artikel.id} value={artikel.id}>
                {artikel.name} ({artikel.artikelnummer})
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
          <input className="input" placeholder="Menge" value={position.menge || ''} onChange={(e) => onChange({ ...position, menge: e.target.value })} />
          <input className="input" placeholder="Einheit" value={position.einheit || ''} onChange={(e) => onChange({ ...position, einheit: e.target.value })} />
          <input className="input" placeholder="Preis pro Einheit" value={position.preis_pro_einheit || ''} onChange={(e) => onChange({ ...position, preis_pro_einheit: e.target.value })} />
          <input className="input" placeholder="Gesamtpreis (optional)" value={position.gesamtpreis || ''} onChange={(e) => onChange({ ...position, gesamtpreis: e.target.value })} />
        </div>

        <textarea className="input" rows={2} placeholder="Bemerkung" value={position.bemerkung || ''} onChange={(e) => onChange({ ...position, bemerkung: e.target.value })} />

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onCancel}><X size={16} /> Abbrechen</button>
          <button className="btn btn-primary" onClick={onSave}><Check size={16} /> Speichern</button>
        </div>
      </div>
    </div>
  );
}

function KeyboardSearchField({
  label,
  value,
  onChange,
  suggestions,
  onSelect,
  inputRef,
  onNext,
  placeholder,
  renderSuggestion,
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const visibleSuggestions = suggestions.slice(0, 8);

  useEffect(() => {
    setActiveIndex(0);
  }, [value, suggestions.length]);

  function choose(index = activeIndex) {
    const suggestion = visibleSuggestions[index];
    if (suggestion) {
      onSelect(suggestion);
      setOpen(false);
      setTimeout(() => onNext?.(), 0);
      return true;
    }
    return false;
  }

  return (
    <div style={{ position: 'relative' }}>
      <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.85rem' }}>
        {label}
      </label>
      <input
        ref={inputRef}
        className="input"
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setActiveIndex((idx) => Math.min(idx + 1, Math.max(visibleSuggestions.length - 1, 0)));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setOpen(true);
            setActiveIndex((idx) => Math.max(idx - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            if (!choose()) {
              onNext?.();
            }
          } else if (e.key === 'Escape') {
            setOpen(false);
          }
        }}
      />
      {open && visibleSuggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 0.25rem)',
            left: 0,
            right: 0,
            background: '#ffffff',
            border: '1px solid #d1d5db',
            borderRadius: '0.5rem',
            boxShadow: '0 12px 28px rgba(15,23,42,0.14)',
            zIndex: 50,
            overflow: 'hidden',
          }}
        >
          {visibleSuggestions.map((suggestion, index) => (
            <button
              key={suggestion.key || suggestion.id || index}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(index)}
              style={{
                width: '100%',
                border: 0,
                borderBottom: index === visibleSuggestions.length - 1 ? 'none' : '1px solid #e5e7eb',
                background: index === activeIndex ? '#dbeafe' : '#ffffff',
                color: '#111827',
                padding: '0.65rem 0.75rem',
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              {renderSuggestion ? renderSuggestion(suggestion) : String(suggestion.label || '')}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function KeyboardMaterialEntryModal({ token, baustellen, artikelOptionen, onClose, onSaved }) {
  const [lastDate, setLastDate] = useState(todayIso());
  const [form, setForm] = useState(() => ({
    ...DEFAULT_POSITION,
    baustelle: '',
    datum: todayIso(),
  }));
  const [baustelleSearch, setBaustelleSearch] = useState('');
  const [artikelSearch, setArtikelSearch] = useState('');
  const [materialSuggestions, setMaterialSuggestions] = useState([]);
  const [materialLoading, setMaterialLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const baustelleRef = useRef(null);
  const datumRef = useRef(null);
  const kategorieRef = useRef(null);
  const bezeichnungRef = useRef(null);
  const artikelRef = useRef(null);
  const mengeRef = useRef(null);
  const einheitRef = useRef(null);
  const preisRef = useRef(null);
  const gesamtRef = useRef(null);
  const bemerkungRef = useRef(null);
  const submitRef = useRef(null);

  const focusNext = useCallback((ref) => {
    setTimeout(() => {
      ref.current?.focus();
      if (typeof ref.current?.select === 'function') {
        ref.current.select();
      }
    }, 0);
  }, []);

  useEffect(() => {
    focusNext(baustelleRef);
  }, [focusNext]);

  useEffect(() => {
    const query = (form.bezeichnung || '').trim();
    if (query.length < 2) {
      setMaterialSuggestions([]);
      setMaterialLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setMaterialLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/baustellen/kostenpositionen/vorschlaege/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            q: query,
            kategorie: form.kategorie || '',
          },
        });
        if (!cancelled) {
          setMaterialSuggestions(response.data.results || []);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Fehler beim Laden der Material-Vorschläge:', error);
          setMaterialSuggestions([]);
        }
      }
      if (!cancelled) setMaterialLoading(false);
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [form.bezeichnung, form.kategorie, token]);

  const baustelleSuggestions = useMemo(() => {
    const query = baustelleSearch.trim().toLowerCase();
    return baustellen
      .filter((entry) => {
        if (!query) return true;
        return [entry.kunde_name, entry.name, entry.nummer]
          .some((value) => String(value || '').toLowerCase().includes(query));
      })
      .slice()
      .sort((a, b) => compareByLabelDe(getBaustelleLabel(a), getBaustelleLabel(b)) || compareByLabelDe(a.nummer, b.nummer))
      .map((entry) => ({ ...entry, key: `baustelle-${entry.id}`, label: getBaustelleLabel(entry) }));
  }, [baustellen, baustelleSearch]);

  const artikelSuggestions = useMemo(() => {
    const query = artikelSearch.trim().toLowerCase();
    return artikelOptionen
      .filter((entry) => {
        if (!query) return true;
        return [entry.name, entry.artikelnummer, entry.einheit, entry.beschreibung]
          .some((value) => String(value || '').toLowerCase().includes(query));
      })
      .slice()
      .sort((a, b) => compareByLabelDe(a.name, b.name) || compareByLabelDe(a.artikelnummer, b.artikelnummer))
      .map((entry) => ({ ...entry, key: `artikel-${entry.id}`, label: getArtikelLabel(entry) }));
  }, [artikelOptionen, artikelSearch]);

  const materialSearchSuggestions = useMemo(() => {
    const query = (form.bezeichnung || '').trim();
    const source = query.length >= 2
      ? materialSuggestions
      : artikelOptionen.map((entry) => ({
        source: 'artikelstamm',
        artikel_id: entry.id,
        artikelnummer: entry.artikelnummer,
        bezeichnung: entry.name,
        einheit: entry.einheit,
        preis_pro_einheit: entry.aktueller_preis?.preis_pro_einheit || '',
      }));
    return source.map((entry, index) => ({
      ...entry,
      key: `${entry.source}-${entry.artikel_id || 'frei'}-${index}`,
      label: entry.bezeichnung || '',
    }));
  }, [artikelOptionen, form.bezeichnung, materialSuggestions]);

  function updateForm(next) {
    setForm((current) => {
      const merged = typeof next === 'function' ? next(current) : next;
      if (merged.gesamtpreis === '' && merged.menge && merged.preis_pro_einheit) {
        return {
          ...merged,
          gesamtpreis: calculateGesamtpreis(merged.menge, merged.preis_pro_einheit),
        };
      }
      return merged;
    });
  }

  function selectBaustelle(baustelle) {
    updateForm((current) => ({ ...current, baustelle: String(baustelle.id) }));
    setBaustelleSearch(getBaustelleLabel(baustelle));
  }

  function selectMaterialSuggestion(suggestion) {
    updateForm((current) => ({
      ...current,
      artikel: suggestion.artikel_id ? String(suggestion.artikel_id) : '',
      bezeichnung: suggestion.bezeichnung || '',
      einheit: suggestion.einheit || '',
      preis_pro_einheit: suggestion.preis_pro_einheit || '',
      gesamtpreis: '',
    }));
    if (suggestion.artikel_id) {
      const article = artikelOptionen.find((entry) => String(entry.id) === String(suggestion.artikel_id));
      setArtikelSearch(getArtikelLabel(article));
    }
  }

  function selectArtikel(artikel) {
    updateForm((current) => ({
      ...current,
      artikel: String(artikel.id),
      bezeichnung: current.bezeichnung || artikel.name || '',
      einheit: current.einheit || artikel.einheit || '',
      preis_pro_einheit: artikel.aktueller_preis?.preis_pro_einheit || current.preis_pro_einheit || '',
      gesamtpreis: '',
    }));
    setArtikelSearch(getArtikelLabel(artikel));
  }

  function resetAfterSave(savedDate, baustelleId) {
    setLastDate(savedDate);
    setForm({
      ...DEFAULT_POSITION,
      baustelle: baustelleId,
      datum: savedDate,
      kategorie: form.kategorie || 'GERAET',
      menge: '',
    });
    setArtikelSearch('');
    setMaterialSuggestions([]);
    focusNext(bezeichnungRef);
  }

  async function submit() {
    if (saving) return;
    const payload = {
      baustelle: Number(form.baustelle),
      datum: form.datum || lastDate || todayIso(),
      kategorie: form.kategorie || 'GERAET',
      artikel: form.artikel ? Number(form.artikel) : null,
      bezeichnung: (form.bezeichnung || '').trim(),
      menge: String(form.menge || '').replace(',', '.'),
      einheit: form.einheit || '',
      preis_pro_einheit: form.preis_pro_einheit === '' ? null : String(form.preis_pro_einheit).replace(',', '.'),
      gesamtpreis: form.gesamtpreis === '' ? null : String(form.gesamtpreis).replace(',', '.'),
      bemerkung: form.bemerkung || '',
    };
    if (!payload.baustelle) {
      alert('Bitte Baustelle auswählen.');
      focusNext(baustelleRef);
      return;
    }
    if (!payload.datum || !payload.kategorie || !payload.menge) {
      alert('Bitte Datum, Kategorie und Menge ausfüllen.');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/baustellen/kostenpositionen/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await onSaved?.(payload.baustelle);
      resetAfterSave(payload.datum, String(payload.baustelle));
    } catch (error) {
      console.error('Fehler beim Speichern der Kostenposition:', error);
      alert(error.response?.data ? JSON.stringify(error.response.data) : 'Fehler beim Speichern der Kostenposition');
    }
    setSaving(false);
  }

  function handleEnterNext(e, ref) {
    if (e.key === 'Enter') {
      e.preventDefault();
      focusNext(ref);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '4vh 1rem',
        overflowY: 'auto',
      }}
    >
      <div className="card" style={{ width: 'min(1120px, 100%)', background: '#ffffff', margin: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.25rem' }}>Tastatur-Erfassung</h3>
            <div style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '0.25rem' }}>
              Pfeiltasten wählen Vorschläge, Enter bestätigt und springt weiter.
            </div>
          </div>
          <button type="button" className="btn" onClick={onClose} tabIndex={-1}>
            <X size={16} /> Schließen
          </button>
        </div>

        <div style={{ display: 'grid', gap: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem' }}>
            <KeyboardSearchField
              label="Baustelle"
              value={baustelleSearch}
              onChange={(value) => {
                setBaustelleSearch(value);
                updateForm((current) => ({ ...current, baustelle: '' }));
              }}
              suggestions={baustelleSuggestions}
              onSelect={selectBaustelle}
              inputRef={baustelleRef}
              onNext={() => focusNext(datumRef)}
              placeholder="Baustelle suchen..."
              renderSuggestion={(suggestion) => (
                <span>
                  <strong>{getBaustelleLabel(suggestion)}</strong>
                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280' }}>{suggestion.nummer || ''}</span>
                </span>
              )}
            />
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.85rem' }}>Datum</label>
              <input
                ref={datumRef}
                type="date"
                className="input"
                value={form.datum || lastDate}
                onChange={(e) => updateForm({ ...form, datum: e.target.value })}
                onKeyDown={(e) => handleEnterNext(e, kategorieRef)}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.85rem' }}>Kategorie</label>
              <select
                ref={kategorieRef}
                className="input"
                value={form.kategorie || 'GERAET'}
                onChange={(e) => updateForm({ ...form, kategorie: e.target.value })}
                onKeyDown={(e) => handleEnterNext(e, bezeichnungRef)}
              >
                {KATEGORIE_OPTIONS.map((entry) => (
                  <option key={entry.value} value={entry.value}>{entry.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: '1rem' }}>
            <KeyboardSearchField
              label="Bezeichnung"
              value={form.bezeichnung || ''}
              onChange={(value) => updateForm({ ...form, bezeichnung: value })}
              suggestions={materialSearchSuggestions}
              onSelect={selectMaterialSuggestion}
              inputRef={bezeichnungRef}
              onNext={() => focusNext(artikelRef)}
              placeholder="Material suchen..."
              renderSuggestion={(suggestion) => (
                <span>
                  <strong>{suggestion.bezeichnung}</strong>
                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280' }}>
                    {suggestion.source === 'artikelstamm' ? 'Artikelstamm' : 'Frühere Eingabe'}
                    {suggestion.artikelnummer ? ` • ${suggestion.artikelnummer}` : ''}
                    {suggestion.einheit ? ` • ${suggestion.einheit}` : ''}
                    {suggestion.preis_pro_einheit ? ` • ${formatAmountDe(suggestion.preis_pro_einheit)} EUR` : ''}
                    {materialLoading ? ' • lädt...' : ''}
                  </span>
                </span>
              )}
            />
            <KeyboardSearchField
              label="Artikelreferenz"
              value={artikelSearch}
              onChange={(value) => {
                setArtikelSearch(value);
                updateForm((current) => ({ ...current, artikel: '' }));
              }}
              suggestions={artikelSuggestions}
              onSelect={selectArtikel}
              inputRef={artikelRef}
              onNext={() => focusNext(mengeRef)}
              placeholder="Artikel suchen oder leer lassen..."
              renderSuggestion={(suggestion) => (
                <span>
                  <strong>{suggestion.name}</strong>
                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#6b7280' }}>
                    {suggestion.artikelnummer || '-'} • {suggestion.einheit || '-'}
                    {suggestion.aktueller_preis?.preis_pro_einheit ? ` • ${formatAmountDe(suggestion.aktueller_preis.preis_pro_einheit)} EUR` : ''}
                  </span>
                </span>
              )}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.85rem' }}>Menge</label>
              <input
                ref={mengeRef}
                className="input"
                value={form.menge || ''}
                onChange={(e) => updateForm({ ...form, menge: e.target.value, gesamtpreis: '' })}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    updateForm({ ...form, menge: stepNumericInput(form.menge, 1), gesamtpreis: '' });
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    updateForm({ ...form, menge: stepNumericInput(form.menge, -1), gesamtpreis: '' });
                  } else {
                    handleEnterNext(e, einheitRef);
                  }
                }}
                placeholder="Menge"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.85rem' }}>Einheit</label>
              <input
                ref={einheitRef}
                className="input"
                value={form.einheit || ''}
                onChange={(e) => updateForm({ ...form, einheit: e.target.value })}
                onKeyDown={(e) => handleEnterNext(e, preisRef)}
                placeholder="Einheit"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.85rem' }}>Preis pro Einheit</label>
              <input
                ref={preisRef}
                className="input"
                value={form.preis_pro_einheit || ''}
                onChange={(e) => updateForm({ ...form, preis_pro_einheit: e.target.value, gesamtpreis: '' })}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    updateForm({ ...form, preis_pro_einheit: stepNumericInput(form.preis_pro_einheit, 1), gesamtpreis: '' });
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    updateForm({ ...form, preis_pro_einheit: stepNumericInput(form.preis_pro_einheit, -1), gesamtpreis: '' });
                  } else {
                    handleEnterNext(e, gesamtRef);
                  }
                }}
                placeholder="Preis pro Einheit"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.85rem' }}>Gesamtpreis</label>
              <input
                ref={gesamtRef}
                className="input"
                value={form.gesamtpreis || ''}
                onChange={(e) => updateForm({ ...form, gesamtpreis: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    updateForm({ ...form, gesamtpreis: stepNumericInput(form.gesamtpreis, 1) });
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    updateForm({ ...form, gesamtpreis: stepNumericInput(form.gesamtpreis, -1) });
                  } else {
                    handleEnterNext(e, bemerkungRef);
                  }
                }}
                placeholder="Gesamtpreis"
              />
            </div>
          </div>

          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.85rem' }}>Bemerkung</label>
            <textarea
              ref={bemerkungRef}
              className="input"
              rows={2}
              value={form.bemerkung || ''}
              onChange={(e) => updateForm({ ...form, bemerkung: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  focusNext(submitRef);
                }
              }}
              placeholder="Bemerkung"
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button
              ref={submitRef}
              type="button"
              className="btn btn-primary"
              onClick={submit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
              disabled={saving}
            >
              <Check size={16} /> {saving ? 'Speichert...' : 'Übernehmen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaterialerfassungView;
